import React, { useEffect, useRef, useState, useMemo, memo, forwardRef, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Hero from './Hero';
import ConversationBubble from './ConversationBubble';
import { addQuery, fetchAnswer, selectQueries, selectStatus, updateQuery, setStatus } from './conversationSlice';
import { openSnackbar } from 'store/slices/snackbar';
import { sendFeedback } from './conversationApi';
import './index.css';
import {
    selectApiKeyStatus,
    selectSelectedDocs,
    selectSourceDocs,
    selectConversations,
    setConversations,
    selectConversationId,
    setSourceDocs,
    setSelectedDocs
} from './preferences/preferenceSlice';
import { setConversation, updateConversationId } from './conversationSlice';
import ConversationTile from './ConversationTile';
import EmbeddingDocsManager from './EmbeddingDocsManager';
import KnowledgeDocsManager from './KnowledgeDocsManager';
import { store } from 'store';
import { authManager } from 'utils/authManager';
import {
    useGetResearchDocsListQuery,
    useGetResearchDocDocumentsQuery,
    useCreateResearchDocMutation,
    useDeleteResearchDocMutation,
    useAskResearchDocMutation,
    useGetResearchConversationsQuery,
    useGetResearchConversationDetailQuery,
    useDeleteResearchConversationMutation,
    useUploadStorageFileMutation
} from 'store/slices/apiSlice';
import apiSlice from 'store/slices/apiSlice';
import MainCard from '../../../ui-component/cards/MainCard';
import { getDocs, getDoc } from './preferences/preferenceApi';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import ContentCopyTwoToneIcon from '@mui/icons-material/ContentCopyTwoTone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
    Box,
    Chip,
    CircularProgress,
    InputAdornment,
    IconButton,
    OutlinedInput,
    Typography,
    Link,
    TextField,
    Autocomplete,
    Tooltip,
    Button,
    Popover,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import ShareIcon from '@mui/icons-material/Share';
import SendTwoToneIcon from '@mui/icons-material/SendTwoTone';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import HistoryIcon from '@mui/icons-material/History';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
// MIGRATED: AWS SDK removed, backend now uses Firebase Storage (GCS)
// Legacy stub so dead S3 callbacks keep compiling and fail gracefully if invoked.
const s3 = {
    upload: (_, cb) => cb && cb(new Error('AWS S3 disabled')),
    deleteObject: (_, cb) => cb && cb(new Error('AWS S3 disabled')),
    deleteObjects: (_, cb) => cb && cb(new Error('AWS S3 disabled')),
    putObject: (_, cb) => cb && cb(new Error('AWS S3 disabled')),
    listObjects: (_, cb) => cb && cb(new Error('AWS S3 disabled'), { Contents: [] }),
    listObjectsV2: (_, cb) => cb && cb(new Error('AWS S3 disabled'), { Contents: [], CommonPrefixes: [] }),
    getObject: (_, cb) => cb && cb(new Error('AWS S3 disabled'), null),
    getSignedUrlPromise: () => Promise.reject(new Error('AWS S3 disabled'))
};

import useAuth from 'hooks/useAuth';
import AddCircleTwoTone from '@mui/icons-material/AddCircleTwoTone';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import { useMediaQuery, useOutsideAlerter } from 'hooks/useMediaQuery';
import MicIcon from '@mui/icons-material/Mic';
import MicRecorder from 'mic-recorder-to-mp3';

import { useSpeechRecognition } from 'react-speech-recognition';
import { fetchVectors, updateModel } from 'store/slices/vector';
import { QRCodeCanvas as QRCode } from 'qrcode.react';

// Vùng scroll: Box có overflowY auto và chiều cao cố định (flex: 1 + minHeight: 0) để scroll bên trong, không dính scroll ngoài
const ScrollArea = ({ children, style, containerRef }) => (
    <Box
        ref={containerRef}
        sx={{
            flex: 1,
            minHeight: 0,
            overflowX: 'hidden',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            ...style
        }}
    >
        {children}
    </Box>
);

/** Input chat với state nội bộ để gõ không re-render cả list tin nhắn dài → tránh chậm / violation */
const ChatInput = memo(forwardRef(function ChatInput({
    onSubmit,
    disabled,
    placeholder,
    status,
    isRecording,
    isMicro,
    onStartRecord,
    onStopRecord,
    showSnackbar,
    inputRef
}, ref) {
    const [value, setValue] = useState('');
    const displayValue = isRecording ? 'Đang nghe...' : value;
    const canSubmit = !!value.trim() && status !== 'loading' && !disabled;

    useImperativeHandle(ref, () => ({ setValue }));

    const handleSubmit = () => {
        if (!value.trim() || status === 'loading' || disabled) {
            if (disabled) showSnackbar?.({ open: true, anchorOrigin: { vertical: 'top', horizontal: 'center' }, message: 'Vui lòng chọn Knowledge hoặc trợ lý!', variant: 'alert', alert: { color: 'error' } });
            return;
        }
        onSubmit(value.trim());
        setValue('');
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            <Tooltip title={disabled ? 'Vui lòng chọn Knowledge hoặc trợ lý ở phía trên để bắt đầu' : ''} placement="top">
                <span style={{ flex: 1, display: 'flex', minWidth: 0 }}>
                    <OutlinedInput
                        multiline
                        maxRows={4}
                        size="small"
                        ref={inputRef}
                        fullWidth
                        disabled={disabled}
                        value={displayValue}
                        onChange={(e) => !isRecording && setValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSubmit();
                            }
                        }}
                        placeholder={disabled ? 'Vui lòng chọn Knowledge hoặc trợ lý ở phía trên để bắt đầu' : 'Nhập tin nhắn... (Enter gửi, Shift+Enter xuống dòng)'}
                        sx={{ borderRadius: 3, bgcolor: disabled ? 'action.hover' : 'grey.50', flex: 1 }}
                        endAdornment={
                            <InputAdornment position="end" sx={{ alignSelf: 'center', mb: 0.5 }}>
                                {isMicro && !isRecording && (
                                    <Tooltip title="Sử dụng micro"><IconButton size="small" onClick={onStartRecord} aria-label="micro"><MicIcon /></IconButton></Tooltip>
                                )}
                                {isMicro && isRecording && (
                                    <Tooltip title="Dừng ghi âm"><IconButton size="small" disabled={status === 'loading'} onClick={onStopRecord} aria-label="dừng"><StopCircleIcon color="error" /></IconButton></Tooltip>
                                )}
                            </InputAdornment>
                        }
                        inputProps={{ 'aria-label': 'Tin nhắn' }}
                    />
                </span>
            </Tooltip>
            <Tooltip title={disabled ? 'Chọn Knowledge hoặc trợ lý trước' : status === 'loading' ? 'Đang xử lý...' : 'Gửi'}>
                <span>
                    <IconButton
                        color="primary"
                        disabled={!canSubmit}
                        onClick={handleSubmit}
                        aria-label="gửi"
                        sx={{
                            bgcolor: canSubmit ? 'primary.main' : undefined,
                            color: canSubmit ? 'primary.contrastText' : undefined,
                            '&:hover': canSubmit ? { bgcolor: 'primary.dark' } : undefined
                        }}
                    >
                        {status === 'loading' ? <CircularProgress size={22} sx={{ color: '#156DA2' }} /> : <SendTwoToneIcon />}
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
}));

const filter = createFilterOptions();
const project = 'document';

// Kiểm tra component undefined (gây lỗi "Element type is invalid")
const _components = [
    ['Hero', Hero],
    ['ConversationBubble', ConversationBubble],
    ['MainCard', MainCard],
    ['ConversationTile', ConversationTile],
    ['EmbeddingDocsManager', EmbeddingDocsManager],
    ['KnowledgeDocsManager', KnowledgeDocsManager],
    ['ScrollArea', ScrollArea],
    ['Box', Box],
    ['Link', Link]
];
_components.forEach(([name, C]) => {
    if (C === undefined) console.error('Conversation: undefined component:', name);
});

/** Các model LLM (API key cấu hình trong backend .env: OPENAI_API_KEY, ANTHROPIC_API_KEY, GOOGLE_GEMINI_API_KEY) */
const KNOWLEDGE_LLM_OPTIONS = [
    // Claude 4.x mới (Anthropic) — qua Max OAuth, KHÔNG cần API key
    { name: 'claude-opus-4-7', label: 'Claude Opus 4.7 (most capable, slow)' },
    { name: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (balanced, recommended)' },
    { name: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (fast, cheap)' },
    // Claude legacy
    { name: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet (legacy)' },
    { name: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku (legacy)' },
    { name: 'claude-3-opus-20240229', label: 'Claude 3 Opus (legacy)' },
    { name: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet (legacy)' },
    // OpenAI
    { name: 'gpt-4o', label: 'OpenAI gpt-4o' },
    { name: 'gpt-4o-mini', label: 'OpenAI gpt-4o-mini' },
    { name: 'gpt-4', label: 'OpenAI gpt-4' },
    { name: 'gpt-4-0613', label: 'OpenAI gpt-4-0613' },
    { name: 'gpt-4-32k', label: 'OpenAI gpt-4-32k' },
    { name: 'gpt-4-32k-0613', label: 'OpenAI gpt-4-32k-0613' },
    { name: 'gpt-4-1106', label: 'OpenAI gpt-4-1106 (trả phí)' },
    { name: 'gpt-3.5-turbo', label: 'OpenAI gpt-3.5-turbo' },
    // Gemini
    { name: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
    { name: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
    { name: 'gemini-1.0-pro', label: 'Gemini 1.0 Pro' }
];

/** Embedding model cho Knowledge — Local (free), OpenAI, Gemini.
 *  bge-m3 = local sentence-transformers, multilingual VI+EN, 1024 dim — Recommended cho VI. */
const EMBEDDING_MODEL_OPTIONS = [
    { value: 'local:bge-m3', label: 'bge-m3 — multilingual VI+EN, 1024d, local, FREE (Recommended)' },
    { value: 'local:bge-large-en', label: 'bge-large-en — English, 1024d, local, FREE' },
    { value: 'local:multilingual-e5-large', label: 'multilingual-e5-large — VI+EN, 1024d, local, FREE' },
    { value: 'local:all-MiniLM-L6-v2', label: 'MiniLM-L6 — English-fast, 384d, local, FREE' },
    { value: '', label: 'Mặc định (Gemini embedding-001)' },
    { value: 'gemini:embedding-001', label: 'Gemini embedding-001 (paid)' },
    { value: 'gemini:text-embedding-004', label: 'Gemini text-embedding-004 (paid)' },
    { value: 'openai:text-embedding-3-small', label: 'OpenAI text-embedding-3-small (paid)' },
    { value: 'openai:text-embedding-3-large', label: 'OpenAI text-embedding-3-large (paid)' },
    { value: 'openai:text-embedding-ada-002', label: 'OpenAI text-embedding-ada-002 (paid)' }
];

/** fullName từ doc: nếu doc là string (id từ URL) thì dùng luôn, nếu là object thì doc.fullName */
function getDocFullName(doc) {
    if (doc == null) return undefined;
    return typeof doc === 'string' ? doc : doc?.fullName;
}

export default function Conversation() {
    // Debug: ném lỗi ngay nếu có component undefined (tránh "Element type is invalid")
    const _check = [
        ['Hero', Hero], ['ConversationBubble', ConversationBubble], ['MainCard', MainCard],
        ['ConversationTile', ConversationTile], ['EmbeddingDocsManager', EmbeddingDocsManager],
        ['KnowledgeDocsManager', KnowledgeDocsManager], ['ScrollArea', ScrollArea],
        ['Link', Link], ['OpenInNewIcon', OpenInNewIcon], ['ContentCopyTwoToneIcon', ContentCopyTwoToneIcon],
        ['AddCircleTwoTone', AddCircleTwoTone], ['ArrowBackIcon', ArrowBackIcon]
    ];
    const _undef = _check.filter(([, C]) => C === undefined).map(([n]) => n);
    if (_undef.length) throw new Error('Conversation undefined components: ' + _undef.join(', '));

    const selectedProject = new URLSearchParams(window.location.search).get('id');
    const queries = useSelector(selectQueries);
    const status = useSelector(selectStatus);
    const dispatch = useDispatch();
    const docs = useSelector(selectSourceDocs);
    const selectedDocs = useSelector(selectSelectedDocs);
    const chatInputRef = useRef(null);
    const endMessageRef = useRef(null);
    const inputRef = useRef(null);
    const conversations = useSelector(selectConversations);
    const conversationId = useSelector(selectConversationId);
    const [isDocsListOpen, setIsDocsListOpen] = useState(false);
    const isApiKeySet = useSelector(selectApiKeyStatus);
    const [apiKeyModalState, setApiKeyModalState] = useState('INACTIVE');
    const [selectedDocsModalState, setSelectedDocsModalState] = useState('ACTIVE');
    const [uploadModalState, setUploadModalState] = useState('INACTIVE');
    const [hasScrolledToLast, setHasScrolledToLast] = useState(true);
    const [scrollEl, setScrollEl] = useState();
    const [doc, setDoc] = useState(selectedProject ? selectedProject : (docs && docs[0]) ? docs[0] : null);
    const [initialDoc, setInitialDoc] = useState(true);
    const [inputDoc, setInputDoc] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [manageDocsOpen, setManageDocsOpen] = useState(false);
    const [viewMode, setViewMode] = useState('knowledge');
    const [selectedKnowledge, setSelectedKnowledge] = useState(null);
    const [createKnowledgeOpen, setCreateKnowledgeOpen] = useState(false);
    const [newKnowledgeName, setNewKnowledgeName] = useState('');
    const [newKnowledgeEmbeddingModel, setNewKnowledgeEmbeddingModel] = useState('');
    const [newKnowledgeLlmModel, setNewKnowledgeLlmModel] = useState('');
    const [manageDocsKnowledge, setManageDocsKnowledge] = useState(null);
    const [model, setModel] = useState(KNOWLEDGE_LLM_OPTIONS[0]);
    const [inputModel, setInputModel] = useState('claude-haiku-4-5');
    const [historySidebarCollapsed, setHistorySidebarCollapsed] = useState(false);
    const { isMobile } = useMediaQuery();
    const [navOpen, setNavOpen] = useState(false);
    const [isBlock, setIsBlock] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [blobURL, setBlobURL] = useState('');
    const [file, setFile] = useState('');
    const { fetchBillings } = useAuth();
    const [recorder, setRecoder] = useState(
        new MicRecorder({
            bitRate: 128
        })
    );
    const [isMicro, setIsMicro] = useState(true);
    const [openModelChangeDialog, setOpenModelChangeDialog] = useState(false);
    const [lastGeneratedFiles, setLastGeneratedFiles] = useState([]);
    const [previewImageUrl, setPreviewImageUrl] = useState(null);
    const navRef = useRef(null);
    const embeddingsName = 'openai_text-embedding-ada-002';
    const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();
    useEffect(() => {
        if (!browserSupportsSpeechRecognition) {
            setIsBlock(true);
            // return <span>Browser doesn't support speech recognition.</span>;
        }
    }, []);

    // Khi đổi project trên URL (?id=...), reset Knowledge đã chọn để tránh hiển thị Knowledge của project cũ
    useEffect(() => {
        setSelectedKnowledge(null);
        setManageDocsKnowledge(null);
        setManageDocsOpen(false);
    }, [selectedProject]);
    const [isCopy, setCopy] = useState(false);
    const [isCopyAPI, setCopyAPI] = useState(false);
    const [modalValueRadio, setModalValueRadio] = useState('gpt-4');
    const modelRef = useRef(model);

    // REMOVED: S3 bucket - using Firebase Storage
    // const S3_BUCKET = 'youteach.ai';
    // REMOVED: AWS region
    // const REGION = 'ap-southeast-1';
    const vectorsState = useSelector((state) => state.vector);
    const [vectors, setVectors] = useState([]);
    const [vector, setVector] = useState([]);
    const [apiKeys, setApiKeys] = useState([]);

    /** Tải lịch sử hội thoại hiện tại (queries) thành file Markdown */
    const downloadHistoryAsMarkdown = () => {
        if (!queries?.length) return;
        const lines = ['# Lịch sử hội thoại', '', `*Xuất lúc: ${new Date().toLocaleString('vi-VN')}*`, ''];
        queries.forEach((q, i) => {
            lines.push('---', '');
            lines.push('## Bạn', '');
            lines.push((q.prompt || '(Không có nội dung)').trim(), '');
            lines.push('## AI', '');
            lines.push((q.response || q.error || '(Đang chờ phản hồi...)').trim(), '');
            lines.push('');
        });
        const text = lines.join('\n');
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const dateStr = new Date().toISOString().slice(0, 19).replace(/T/g, '-').replace(/:/g, '');
        const titleSlug = selectedKnowledge && conversationId && researchConversations?.find((c) => c.id === conversationId)?.title
            ? String(researchConversations.find((c) => c.id === conversationId).title).replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_\u0080-\uFFFF]/g, '').slice(0, 40)
            : '';
        a.download = titleSlug ? `lich-su-${titleSlug}-${dateStr}.txt` : `lich-su-chat-${dateStr}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    //download QR Code
    const downloadQR = () => {
        const canvas = document.getElementById('qrcode');
        const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
        let downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = 'youteach-qrcode-sharebot.png';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    };

    // REMOVED: AWS configuration
    // AWS.config.update({
    //         accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY,
    //         secretAccessKey: process.env.REACT_APP_AWS_SECRET_KEY,
    //         s3Url: 'http://kmou-dp.s3-website-ap-southeast-1.amazonaws.com',
    //         bucketName: S3_BUCKET,
    //         region: REGION
    //     });

    // REMOVED: S3 client - now using Firebase Storage
    // const s3 = new AWS.S3();

    const navigate = useNavigate();

    useEffect(() => {
        async function requestDocs() {
            const data = await getDocs();
            dispatch(setSourceDocs(data));
        }
        requestDocs();
        dispatch(setConversation([]));
        dispatch(
            updateConversationId({
                query: { conversationId: null }
            })
        );
    }, []);

    useEffect(() => {
        const fullName = getDocFullName(doc);
        dispatch(fetchVectors(fullName ? fullName + '/' : ''));
    }, [dispatch, doc]);

    useEffect(() => {
        setVector(vectorsState?.vectors?.id);
        setVectors(vectorsState?.vectors ?? []);
        setApiKeys(vectorsState?.apikeys ?? []);
        if (vectorsState?.vectors?.model) {
            const opt = KNOWLEDGE_LLM_OPTIONS.find((o) => o.name === vectorsState?.vectors?.model);
            setModel(opt || { name: vectorsState?.vectors?.model, label: vectorsState?.vectors?.model });
        }
    }, [vectorsState?.vectors?.id, vectorsState?.vectors, vectorsState?.apikeys]);

    useEffect(() => {
        if (typeof fetchBillings === 'function') {
            fetchBillings();
        }
    }, [queries.length, queries[queries.length - 1]]);

    useEffect(() => {
        const element = document.getElementById('inputbox');
        if (element) {
            element.focus();
        }
    }, []);

    useEffect(() => {
        if (typeof doc === 'string' && initialDoc) {
            async function requestDoc() {
                const data = await getDoc();
                if (data != null && Array.isArray(data) && data.length > 0) {
                    localStorage.setItem('DocsGPTRecentDocs', JSON.stringify(data[0]));
                    setDoc(data[0]);
                    setInitialDoc(false);
                }
            }

            requestDoc();
        } else if (doc?.model === embeddingsName) {
            dispatch(setSelectedDocs(doc));
        }
    }, [doc, initialDoc]);
    // useEffect(() => {
    //     const observerCallback = (entries) => {
    //         entries.forEach((entry) => {
    //             setHasScrolledToLast(entry.isIntersecting);
    //         });
    //     };

    //     const observer = new IntersectionObserver(observerCallback, {
    //         root: null,
    //         threshold: [1, 0.8]
    //     });
    //     if (endMessageRef.current) {
    //         observer.observe(endMessageRef.current);
    //     }

    //     return () => {
    //         observer.disconnect();
    //     };
    // }, [endMessageRef.current]);

    // const scrollIntoView = () => {
    //     endMessageRef?.current?.scrollIntoView({
    //         behavior: 'smooth',
    //         block: 'start'
    //     });
    // };

    const { data: knowledges = [], isLoading: isLoadingKnowledges } = useGetResearchDocsListQuery(
        selectedProject ? { project_name: selectedProject } : {},
        { skip: viewMode !== 'knowledge' }
    );
    const [createKnowledge] = useCreateResearchDocMutation();
    const [deleteKnowledge] = useDeleteResearchDocMutation();
    const [askResearchDoc] = useAskResearchDocMutation();
    const { data: researchConversations = [], refetch: refetchResearchConversations } = useGetResearchConversationsQuery(
        selectedKnowledge?.id ?? null,
        { skip: !selectedKnowledge?.id }
    );
    const [deleteResearchConversation] = useDeleteResearchConversationMutation();
    const [uploadStorageFile] = useUploadStorageFileMutation();
    const [attachmentFiles, setAttachmentFiles] = useState([]); // { id, name }[]
    const attachmentFileInputRef = useRef(null);
    const [editFileId, setEditFileId] = useState(null); // storage_file_id khi muốn chỉnh sửa file theo prompt
    const { data: knowledgeDocuments = [] } = useGetResearchDocDocumentsQuery(selectedKnowledge?.id ?? null, { skip: !selectedKnowledge?.id });
    const showSnackbar = (opts) => {
        try {
            if (typeof openSnackbar === 'function') dispatch(openSnackbar(opts));
        } catch (_) {}
    };

    const baseUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');

    useEffect(() => {
        const preview = lastGeneratedFiles?.find((f) => f.preview && f.storage_file_id);
        if (!preview?.storage_file_id || !baseUrl) {
            setPreviewImageUrl((prev) => { if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev); return null; });
            return;
        }
        const url = `${baseUrl}/storage/files/${preview.storage_file_id}/download`;
        const token = authManager.getToken();
        if (!token) {
            setPreviewImageUrl(url);
            return;
        }
        let revoked = false;
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => (r.ok ? r.blob() : null))
            .then((blob) => (blob ? URL.createObjectURL(blob) : null))
            .then((u) => { if (!revoked) setPreviewImageUrl(u); else if (u?.startsWith('blob:')) URL.revokeObjectURL(u); })
            .catch(() => { if (!revoked) setPreviewImageUrl(url); });
        return () => { revoked = true; setPreviewImageUrl((prev) => { if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev); return null; }); };
    }, [lastGeneratedFiles, baseUrl]);

    useEffect(() => {
        if (!conversationId && selectedKnowledge) {
            setLastGeneratedFiles([]);
            setPreviewImageUrl(null);
        }
    }, [conversationId, selectedKnowledge]);

    const handleQuestion = async (question, model) => {
        question = (question || '').trim();
        if (question === '') return;
        setLastGeneratedFiles([]);
        if (selectedKnowledge) {
            const baseIndex = queries.length;
            dispatch(addQuery({ prompt: question, response: null, sources: [] }));
            dispatch(setStatus('loading'));
            setAttachmentFiles([]);
            if (editFileId) setEditFileId(null);
            const attachmentIds = attachmentFiles.map((a) => a.id);
            const body = {
                query: question,
                chat_history: queries.slice(-6).map((q) => ({ role: q.response ? 'assistant' : 'user', content: q.response || q.prompt })),
                top_k: 5,
                llm_model_override: model?.name || (typeof model === 'object' ? model?.label : model) || 'gpt-4',
                conversation_id: conversationId || undefined,
                save_response: true,
                attachment_file_ids: attachmentIds.length ? attachmentIds : undefined,
                edit_file_id: editFileId || undefined
            };
            const useStream = true;
            if (useStream) {
                try {
                    const token = authManager.getToken();
                    const res = await fetch(`${baseUrl}/research-docs/${selectedKnowledge.id}/ask/stream`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                        body: JSON.stringify(body)
                    });
                    if (!res.ok) throw new Error(res.statusText);
                    const reader = res.body.getReader();
                    const decoder = new TextDecoder();
                    let buffer = '';
                    let accumulated = '';
                    const STREAM_THROTTLE_MS = 40;
                    let lastStreamUpdate = 0;
                    const flushStreamUpdate = () => {
                        dispatch(updateQuery({ index: baseIndex, query: { response: accumulated, sources: [] } }));
                        requestAnimationFrame(() => {
                            const el = scrollContainerRef.current;
                            if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
                        });
                    };
                    while (true) {
                        const { value, done } = await reader.read();
                        if (done) break;
                        buffer += decoder.decode(value, { stream: true });
                        const parts = buffer.split('\n\n');
                        buffer = parts.pop() || '';
                        for (const part of parts) {
                            const line = part.split('\n').find((l) => l.startsWith('data: '));
                            if (!line) continue;
                            try {
                                const data = JSON.parse(line.slice(6));
                                if (data.chunk) {
                                    accumulated += data.chunk;
                                    const now = Date.now();
                                    if (now - lastStreamUpdate >= STREAM_THROTTLE_MS) {
                                        lastStreamUpdate = now;
                                        flushStreamUpdate();
                                    }
                                }
                                if (data.done) {
                                    const sourcesPrepped = (data.sources || []).map((s) =>
                                        s?.content_preview ? { title: s?.metadata?.file_name || 'Nguồn', text: s.content_preview } : { title: 'Nguồn', text: s?.content_preview || '' }
                                    );
                                    dispatch(updateQuery({ index: baseIndex, query: { response: accumulated, sources: sourcesPrepped } }));
                                    requestAnimationFrame(() => {
                                        const el = scrollContainerRef.current;
                                        if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
                                    });
                                    if (data.conversation_id) {
                                        dispatch(updateConversationId({ query: { conversationId: data.conversation_id } }));
                                        refetchResearchConversations();
                                    }
                                    if (data.generated_files?.length) {
                                        setLastGeneratedFiles(data.generated_files);
                                        const names = data.generated_files.map((f) => f?.filename || f).filter(Boolean).join(', ');
                                        showSnackbar({
                                            open: true,
                                            message: `Đã lưu ${data.generated_files.length} file vào Knowledge: ${names}`,
                                            variant: 'alert',
                                            alert: { color: 'success' }
                                        });
                                    } else {
                                        setLastGeneratedFiles([]);
                                    }
                                }
                            } catch (_) {}
                        }
                    }
                } catch (streamErr) {
                    try {
                        const res = await store.dispatch(apiSlice.endpoints.askResearchDoc.initiate({
                            collectionId: selectedKnowledge.id,
                            ...body
                        })).unwrap();
                        const sourcesPrepped = (res.sources || []).map((s) =>
                            s?.content_preview ? { title: s.metadata?.file_name || 'Nguồn', text: s.content_preview } : s
                        );
                        dispatch(updateQuery({ index: baseIndex, query: { response: res.answer, sources: sourcesPrepped } }));
                        if (res.conversation_id) {
                            dispatch(updateConversationId({ query: { conversationId: res.conversation_id } }));
                            refetchResearchConversations();
                        }
                        if (res.generated_files?.length) {
                            setLastGeneratedFiles(res.generated_files);
                            const names = res.generated_files.map((f) => f?.filename || f).filter(Boolean).join(', ');
                            showSnackbar({
                                open: true,
                                message: `Đã lưu ${res.generated_files.length} file vào Knowledge: ${names}`,
                                variant: 'alert',
                                alert: { color: 'success' }
                            });
                        } else setLastGeneratedFiles([]);
                    } catch (err) {
                        dispatch(updateQuery({ index: baseIndex, query: { error: err?.data?.detail || streamErr?.message || 'Lỗi hỏi đáp.' } }));
                    }
                }
            } else {
                try {
                    const res = await store.dispatch(apiSlice.endpoints.askResearchDoc.initiate({
                        collectionId: selectedKnowledge.id,
                        ...body
                    })).unwrap();
                    const sourcesPrepped = (res.sources || []).map((s) =>
                        s?.content_preview ? { title: s.metadata?.file_name || 'Nguồn', text: s.content_preview } : s
                    );
                    dispatch(updateQuery({ index: baseIndex, query: { response: res.answer, sources: sourcesPrepped } }));
                    if (res.conversation_id) {
                        dispatch(updateConversationId({ query: { conversationId: res.conversation_id } }));
                        refetchResearchConversations();
                    }
                    if (res.generated_files?.length) {
                        setLastGeneratedFiles(res.generated_files);
                        const names = res.generated_files.map((f) => f?.filename || f).filter(Boolean).join(', ');
                        showSnackbar({
                            open: true,
                            message: `Đã lưu ${res.generated_files.length} file vào Knowledge: ${names}`,
                            variant: 'alert',
                            alert: { color: 'success' }
                        });
                    } else setLastGeneratedFiles([]);
                } catch (err) {
                    dispatch(updateQuery({ index: baseIndex, query: { error: err?.data?.detail || 'Lỗi hỏi đáp.' } }));
                }
            }
            dispatch(setStatus('idle'));
            return;
        }
        dispatch(addQuery({ prompt: question }));
        dispatch(fetchAnswer({ question, model: model?.name || model }));
    };

    const handleFeedback = (query, feedback, index) => {
        const prevFeedback = query.feedback;
        dispatch(updateQuery({ index, query: { feedback } }));
        sendFeedback(query.prompt, query.response, feedback).catch(() => {
            dispatch(updateQuery({ index, query: { feedback: prevFeedback } }));
        });
    };

    const prepResponseView = (query, index) => {
        let responseView;
        if (query.error) {
            responseView = (
                <ConversationBubble
                    ref={endMessageRef}
                    className={`${index === queries.length - 1 ? 'mb-2' : 'mb-2'}`}
                    key={`${index}ERROR`}
                    message={query.error}
                    type="ERROR"
                ></ConversationBubble>
            );
        } else if (query.response) {
            responseView = (
                <ConversationBubble
                    ref={endMessageRef}
                    className={`${index === queries.length - 1 ? 'mb-2' : 'mb-2'}`}
                    key={`${index}ANSWER`}
                    message={query.response}
                    type={'ANSWER'}
                    sources={query.sources || []}
                    feedback={query.feedback}
                    handleFeedback={(feedback) => handleFeedback(query, feedback, index)}
                    isTheLast={index === queries.length - 1}
                ></ConversationBubble>
            );
        }
        return responseView;
    };

    const handlePaste = (e) => {
        e.preventDefault();
        // const text = e.clipboardData.getData('text/plain');
        // Document.execCommand("insertText", false, text)
    };

    const theme = useTheme();

    useEffect(() => {
        fetchConversations();
    }, []);

    useEffect(() => {
        if (!conversations) {
            fetchConversations();
        }
    }, [conversations, dispatch]);

    const scrollRefChat = useRef();
    /** Ref tới vùng scroll chat (để scroll mượt khi streaming và nút lên/xuống) */
    const scrollContainerRef = useRef(null);

    useMemo(() => {
        if (scrollEl) {
            scrollEl.scrollTop = scrollRefChat?.current?.offsetTop;
        }
    }, [scrollEl, queries]);

    async function fetchConversations() {
        try {
            const res = await store.dispatch(
                apiSlice.endpoints.getConversationsList.initiate({
                    project,
                    user_token: localStorage.getItem('user_token')
                })
            );
            if (res.data) dispatch(setConversations(res.data));
        } catch (error) {
            console.error('Failed to fetch conversations: ', error);
        }
    }

    const handleDeleteConversation = (id) => {
        store
            .dispatch(apiSlice.endpoints.deleteConversation.initiate(id))
            .then(() => fetchConversations())
            .catch((error) => console.error(error));
    };

    const handleDeleteClick = (index, doc) => {
        const docPath = 'indexes/' + 'local' + '/' + localStorage.getItem('user_token') + '/' + project + '/' + doc?.name;

        store
            .dispatch(apiSlice.endpoints.getDeleteOld.initiate(docPath))
            .then(() => {
                // remove the image element from the DOM
                const imageElement = document.querySelector(`#img-${index}`);
                const parentElement = imageElement.parentNode;
                parentElement.parentNode?.removeChild(parentElement);

                let params = {
                    Bucket: 'youteach.ai',
                    Prefix: localStorage.getItem('user_token') + '/' + project + '/' + doc?.name
                };

                s3.listObjects(params, function (err, data) {
                    if (err) return console.log(err);

                    if (data.Contents.length == 0) {
                        s3.deleteObjects(params, function (err, data) {
                            if (err) return console.log(err);
                            else {
                                console.log('Delete Success');
                            }
                        });
                    } else {
                        data.Contents.forEach(function (content) {
                            params = { Bucket: 'youteach.ai' };
                            params.Delete = { Objects: [] };
                            params.Delete.Objects.push({ Key: content.Key });
                        });

                        s3.deleteObjects(params, function (err, data) {
                            if (err) return console.log(err);
                            else {
                                console.log('Delete Success');
                            }
                        });
                    }
                });

                // const params = {
                //   Bucket: 'youteach.ai',
                //   Key: localStorage.getItem('user_token') + '/document/' + doc.name
                // };

                // s3.deleteObject(params, function(err, data) {
                //   if (err) console.log(err, err.stack); // an error occurred
                //   else     console.log("success");           // successful response
                // });
            })
            .catch((error) => console.error(error));
    };

    const handleConversationClick = (index) => {
        store
            .dispatch(apiSlice.endpoints.getConversation.initiate(index))
            .then((res) => {
                if (!res.data) return;
                navigate('/workspace/' + project);
                dispatch(setConversation(res.data));
                dispatch(
                    updateConversationId({
                        query: { conversationId: index }
                    })
                );
            });
    };

    const handleResearchConversationClick = async (conversationId) => {
        setLastGeneratedFiles([]);
        setPreviewImageUrl(null);
        try {
            const res = await store.dispatch(
                apiSlice.endpoints.getResearchConversationDetail.initiate(conversationId)
            );
            const data = res.data;
            if (!data?.messages?.length) {
                dispatch(setConversation([]));
                dispatch(updateConversationId({ query: { conversationId } }));
                return;
            }
            const queriesFromMessages = [];
            const role = (r) => {
                const x = (r || '').toLowerCase().trim();
                return x === 'human' ? 'user' : x;
            };
            const toStr = (c) => {
                if (c == null) return '';
                if (typeof c === 'string') return c.trim();
                if (typeof c === 'object' && c !== null && typeof c.text === 'string') return c.text.trim();
                return String(c).trim();
            };
            for (let i = 0; i < data.messages.length; i++) {
                const m = data.messages[i];
                const r = role(m.role);
                const contentStr = toStr(m.content) || '(Câu hỏi)';
                if (r === 'user') {
                    const next = data.messages[i + 1];
                    const isNextAssistant = role(next?.role) === 'assistant';
                    const sources = isNextAssistant && Array.isArray(next?.sources)
                        ? next.sources.map((s) => ({
                            title: s?.metadata?.file_name || s?.title || 'Nguồn',
                            text: s?.content_preview ?? s?.text ?? ''
                        }))
                        : [];
                    queriesFromMessages.push({
                        prompt: contentStr,
                        response: isNextAssistant ? (toStr(next.content) || '') : null,
                        sources,
                        feedback: null
                    });
                    if (isNextAssistant) i++;
                } else if (r === 'assistant') {
                    const sources = Array.isArray(m.sources)
                        ? (m.sources || []).map((s) => ({
                            title: s?.metadata?.file_name || s?.title || 'Nguồn',
                            text: s?.content_preview ?? s?.text ?? ''
                        }))
                        : [];
                    queriesFromMessages.push({
                        prompt: '(Câu hỏi)',
                        response: contentStr,
                        sources,
                        feedback: null
                    });
                }
            }
            dispatch(setConversation(queriesFromMessages));
            dispatch(updateConversationId({ query: { conversationId } }));
            const withPreview = (gf) => Array.isArray(gf) && gf.some((f) => f?.preview && f?.storage_file_id);
            const lastWithFiles = [...(data.messages || [])].reverse().find((m) => m.role === 'assistant' && withPreview(m.generated_files));
            if (lastWithFiles?.generated_files) setLastGeneratedFiles(lastWithFiles.generated_files);
        } catch (e) {
            console.error('Failed to load research conversation:', e);
        }
    };

    async function updateConversationName(updatedConversation) {
        try {
            const res = await store.dispatch(
                apiSlice.endpoints.updateConversation.initiate({
                    id: updatedConversation.id,
                    ...updatedConversation
                })
            );
            if (res?.data) {
                navigate('/workspace/' + project);
                fetchConversations();
            }
        } catch (err) {
            console.error(err);
        }
    }
    useOutsideAlerter(navRef, () => {
        if (isMobile && navOpen && apiKeyModalState === 'INACTIVE') {
            setNavOpen(false);
            setIsDocsListOpen(false);
        }
    }, [navOpen, isDocsListOpen, apiKeyModalState]);

    /*
        Needed to fix bug where if mobile nav was closed and then window was resized to desktop, nav would still be closed but the button to open would be gone, as per #1 on issue #146
      */

    useEffect(() => {
        setNavOpen(!isMobile);
    }, [isMobile]);

    async function callWhisper(file) {
        // console.log('before call');
        const fileUri = URL.createObjectURL(file);
        const formData = new FormData();
        // formData.append('model', 'whisper-1');
        formData.append('file', file);
        formData.append('api_key', 'kspwKCsQoYJA6GSH4ydKYjdyGveQzMiDpinxAOrEw');
        // formData.append('response_format', 'text');
        // const res = await fetch('https://api.openai.com/v1/audio/transcriptions', {
        //     method: 'POST',
        //     body: formData,
        //     headers: {
        //         Authorization: 'Bearer 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'
        //     }
        // });

        const res = await store.dispatch(apiSlice.endpoints.postS2tWhisper.initiate(formData));
        if (res.error) throw new Error(res.error?.data?.detail || 'S2T failed');
        const text = res.data || {};
        // console.log(text);
        chatInputRef.current?.setValue?.(text['answer'] ?? '');
        // console.log('callChatGPTRes', res.status);
        // console.log('callChatGPTRes', text);
        return text['answer'];
    }

    const start = () => {
        // console.log(recorder);
        if (isBlock || !recorder) {
            console.log('Permission Denied or Recorder not initialized');
        } else {
            recorder
                .start()
                .then(() => {
                    setIsRecording(true);
                })
                .catch((e) => console.error(e));
        }
    };

    const stop = async () => {
        if (isRecording === true && recorder) {
            try {
                const [buffer, blob] = await recorder.stop().getMp3();
                const file = new File(buffer, 'speech.mp3', {
                    type: blob.type,
                    lastModified: Date.now()
                });
                const blobUrl = URL.createObjectURL(file);
                await callWhisper(file, file.name);

                setIsRecording(false);
            } catch (e) {
                console.log(e);
            }
        }
    };

    const handleShareClick = (event) => {
        setAnchorEl(event.currentTarget);
        setVector(vectorsState?.vectors?.id);
        dispatch(fetchVectors((getDocFullName(doc) || '') + '/'));
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id_sharing = open ? 'share-popover' : undefined;

    const handleCopyLink = () => {
        const link = `https://youteach.ai/pages/bot?id=${String(vector)}&project=${(getDocFullName(doc) || '').replaceAll('/', '')}`;
        navigator.clipboard.writeText(link).then(() => {
            setCopy(true);
            setTimeout(() => {
                setCopy(false);
            }, 2000);
        });
    };

    const handleModelChange = (e, model) => {
        modelRef.current = model;
        setOpenModelChangeDialog(true);
    };

    const handleSubmitModelChange = () => {
        // get project storage
        setModel(modelRef.current);
        dispatch(updateModel((getDocFullName(doc) || '').replaceAll('/', ''), vector, project, modelRef.current.name));
        setOpenModelChangeDialog(false);
        dispatch(
            openSnackbar({
                open: true,
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
                message: 'Cập nhật Model thành công',
                variant: 'alert',
                alert: {
                    color: 'success'
                }
            })
        );
    };

    const handleCloseModelChange = () => {
        setOpenModelChangeDialog(false);
    };

    const canSend = doc != null && (typeof doc !== 'object' || doc?.name !== 'default') && (typeof doc?.fullName !== '');
    const canSendKnowledge = !!selectedKnowledge;

    const handleCreateKnowledge = async () => {
        const name = (newKnowledgeName || '').trim();
        if (!name) return;
        try {
            const body = { name };
            if (selectedProject) body.project_name = selectedProject;
            if (newKnowledgeEmbeddingModel) body.embedding_model = newKnowledgeEmbeddingModel;
            if (newKnowledgeLlmModel) body.llm_model = newKnowledgeLlmModel;
            await createKnowledge(body).unwrap();
            setCreateKnowledgeOpen(false);
            setNewKnowledgeName('');
            setNewKnowledgeEmbeddingModel('');
            setNewKnowledgeLlmModel('');
            showSnackbar({ open: true, message: 'Đã tạo Knowledge.', variant: 'alert', alert: { color: 'success' } });
        } catch (err) {
            showSnackbar({ open: true, message: err?.data?.detail || 'Tạo thất bại.', variant: 'alert', alert: { color: 'error' } });
        }
    };

    const handleOpenManageDocs = (k) => {
        setManageDocsKnowledge(k);
        setManageDocsOpen(true);
    };

    if (viewMode === 'knowledge') {
        return (
            <MainCard contentSX={{ p: 0 }} sx={{ bgcolor: 'grey.50', minHeight: 'calc(100vh - 80px)' }}>
                {/* Sprint 8 (2026-05-21): Deprecation banner — module document/ legacy
                    (research_docs API + ChromaDB). User nên chuyển sang /workspace/knowledge
                    đã tách module riêng dùng Qdrant + bge-m3. */}
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        bgcolor: 'warning.lighter',
                        borderBottom: '1px solid',
                        borderColor: 'warning.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap'
                    }}
                >
                    <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                        Module cũ — đang chuyển sang trang Knowledge mới (Qdrant + bge-m3 + reranker). Dữ liệu cũ vẫn truy cập được, upload mới khuyến nghị qua trang mới.
                    </Typography>
                    <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        onClick={() => navigate('/workspace/knowledge')}
                    >
                        Sang Knowledge mới
                    </Button>
                </Box>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>Knowledge – Hỏi đáp tài liệu</Typography>
                    <Button variant="contained" startIcon={<AddCircleTwoTone />} onClick={() => setCreateKnowledgeOpen(true)}>Tạo Knowledge mới</Button>
                </Box>
                <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Chọn một Knowledge để vào chat hoặc quản lý tài liệu. Mỗi Knowledge có bộ tài liệu riêng và embedding tương ứng.</Typography>
                    {isLoadingKnowledges ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, py: 4 }}>
                            <CircularProgress size={48} thickness={4} sx={{ color: 'primary.main' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Đang tải danh sách Knowledge...</Typography>
                        </Box>
                    ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {(knowledges || []).map((k) => (
                            <Box
                                key={k.id}
                                sx={{
                                    minWidth: 260,
                                    p: 2,
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1
                                }}
                            >
                                <Typography variant="subtitle1" fontWeight={600}>{k.name}</Typography>
                                <Typography variant="body2" color="text.secondary">{k.document_count ?? 0} tài liệu</Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    <Button size="small" variant="contained" onClick={() => { setSelectedKnowledge(k); setViewMode('chat'); dispatch(setConversation([])); }}>Vào chat</Button>
                                    <Button size="small" variant="outlined" onClick={() => handleOpenManageDocs(k)}>Quản lý tài liệu</Button>
                                    <Tooltip title="Xóa Knowledge">
                                        <IconButton size="small" color="error" onClick={async () => { if (window.confirm('Xóa Knowledge này?')) try { await deleteKnowledge(k.id).unwrap(); showSnackbar({ open: true, message: 'Đã xóa.', variant: 'alert', alert: { color: 'success' } }); } catch (e) { showSnackbar({ open: true, message: e?.data?.detail || 'Lỗi', variant: 'alert', alert: { color: 'error' } }); } }}><DeleteOutlineIcon /></IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                    )}
                </Box>
                <Dialog open={createKnowledgeOpen} onClose={() => { setCreateKnowledgeOpen(false); setNewKnowledgeEmbeddingModel(''); setNewKnowledgeLlmModel(''); }}>
                    <DialogTitle>Tạo Knowledge mới</DialogTitle>
                    <DialogContent>
                        <DialogContentText>Đặt tên cho Knowledge. Tùy chọn: chọn Embedding model (tạo vector từ tài liệu) và LLM model (trả lời chat). API key cấu hình trong backend .env.</DialogContentText>
                        <TextField autoFocus fullWidth label="Tên Knowledge" value={newKnowledgeName} onChange={(e) => setNewKnowledgeName(e.target.value)} sx={{ mt: 1 }} />
                        <Autocomplete
                            size="small"
                            sx={{ mt: 2 }}
                            options={EMBEDDING_MODEL_OPTIONS}
                            getOptionLabel={(o) => o.label || ''}
                            value={EMBEDDING_MODEL_OPTIONS.find((o) => o.value === newKnowledgeEmbeddingModel) || EMBEDDING_MODEL_OPTIONS[0]}
                            onChange={(e, v) => setNewKnowledgeEmbeddingModel(v?.value ?? '')}
                            renderInput={(params) => <TextField {...params} label="Embedding model (tùy chọn)" />}
                        />
                        <Autocomplete
                            size="small"
                            sx={{ mt: 2 }}
                            options={KNOWLEDGE_LLM_OPTIONS}
                            getOptionLabel={(o) => o.label || ''}
                            value={KNOWLEDGE_LLM_OPTIONS.find((o) => o.name === newKnowledgeLlmModel) || null}
                            onChange={(e, v) => setNewKnowledgeLlmModel(v?.name ?? '')}
                            renderInput={(params) => <TextField {...params} label="LLM model mặc định (tùy chọn)" />}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCreateKnowledgeOpen(false)}>Hủy</Button>
                        <Button onClick={handleCreateKnowledge} variant="contained" color="primary" disabled={!newKnowledgeName?.trim()}>Tạo</Button>
                    </DialogActions>
                </Dialog>
                <KnowledgeDocsManager open={manageDocsOpen} onClose={() => { setManageDocsOpen(false); setManageDocsKnowledge(null); }} knowledge={manageDocsKnowledge} projectId={project} />
            </MainCard>
        );
    }

    return (
        <MainCard
            contentSX={{
                p: 0,
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden'
            }}
            sx={{ bgcolor: 'grey.50', minHeight: 'calc(100vh - 80px)', height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    flexWrap: 'wrap'
                }}
            >
                {selectedKnowledge && (
                    <Tooltip title="Quay lại danh sách Knowledge">
                        <IconButton size="small" onClick={() => { setViewMode('knowledge'); setSelectedKnowledge(null); }} aria-label="quay lại"><ArrowBackIcon /></IconButton>
                    </Tooltip>
                )}
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mr: 1 }}>
                    {selectedKnowledge ? '' : 'Hỏi đáp tài liệu'}
                </Typography>
                {selectedKnowledge && (
                    <Chip
                        label={selectedKnowledge.name}
                        size="small"
                        onDelete={() => { setViewMode('knowledge'); setSelectedKnowledge(null); }}
                        sx={{ fontWeight: 500 }}
                    />
                )}
                {!selectedKnowledge && <Autocomplete
                    size="small"
                    sx={{ minWidth: 180 }}
                    options={docs || []}
                    getOptionLabel={(option) => (option?.name ? option.name : typeof option === 'string' ? option : '')}
                    getOptionDisabled={(option) => option?.name === 'default'}
                    isOptionEqualToValue={(option, value) => option?.name === value?.name}
                    renderInput={(params) => <TextField {...params} label="Trợ lý / Dự án" />}
                    value={doc?.name === 'default' ? null : (doc && typeof doc === 'object' ? doc : { name: getDocFullName(doc), fullName: getDocFullName(doc) })}
                    onChange={(e, newDoc) => setDoc(newDoc)}
                    inputValue={inputDoc}
                    onInputChange={(e, v) => setInputDoc(v)}
                />}
                <Autocomplete
                    size="small"
                    sx={{ minWidth: 220 }}
                    disabled={!canSend && !canSendKnowledge}
                    getOptionLabel={(option) => option?.label || ''}
                    isOptionEqualToValue={(option, value) => option?.name === value?.name}
                    options={KNOWLEDGE_LLM_OPTIONS}
                    getOptionDisabled={(option) => option.name === 'gpt-4-1106' && localStorage.getItem('user_type') === 'trial'}
                    renderInput={(params) => <TextField {...params} label="Mô hình" />}
                    value={(docs?.[0]?.fullName === 'default' && doc?.fullName === 'default') || doc == null || (typeof doc === 'object' && doc?.fullName === '') ? null : model}
                    onChange={(e, v) => handleModelChange(e, v)}
                    inputValue={inputModel}
                    onInputChange={(e, v) => setInputModel(v)}
                />
                <Box sx={{ flex: 1, minWidth: 80 }} />
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddCircleTwoTone />}
                    onClick={() => {
                        dispatch(setConversation([]));
                        dispatch(updateConversationId({ query: { conversationId: null } }));
                    }}
                >
                    Cuộc trò chuyện mới
                </Button>
                <Tooltip title="Tải lịch sử hội thoại dưới dạng file văn bản (.txt)">
                    <span>
                        <IconButton
                            size="small"
                            disabled={!queries?.length}
                            onClick={downloadHistoryAsMarkdown}
                            aria-label="Tải Markdown"
                        >
                            <DownloadIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Quản lý tài liệu embedding">
                    <span>
                        <IconButton size="small" disabled={!canSend && !canSendKnowledge} onClick={() => { setManageDocsKnowledge(selectedKnowledge || doc); setManageDocsOpen(true); }} aria-label="Quản lý tài liệu">
                            <FolderOpenIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Chia sẻ trợ lý">
                    <span>
                        <IconButton size="small" disabled={!canSend} onClick={handleShareClick} aria-label="Chia sẻ" sx={{ display: selectedKnowledge ? 'none' : undefined }}>
                            <ShareIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Dialog open={openModelChangeDialog} onClose={handleCloseModelChange}>
                    <DialogTitle>Xác nhận đổi mô hình</DialogTitle>
                    <DialogContent>
                        <DialogContentText>Chọn mô hình khác có thể ảnh hưởng đến ngữ cảnh cuộc trò chuyện.</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseModelChange}>Hủy</Button>
                        <Button onClick={handleSubmitModelChange} variant="contained" color="primary">Đồng ý</Button>
                    </DialogActions>
                </Dialog>
                <Popover open={open} anchorEl={anchorEl} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                    <Box sx={{ p: 2, minWidth: 360 }}>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>Chia sẻ trợ lý</Typography>
                        {QRCode && <QRCode level="H" id="qrcode" value={'https://youteach.ai/pages/bot?id=' + String(vector) + '&project=' + (getDocFullName(doc) || '').replaceAll('/', '')} size={180} includeMargin />}
                        <Link component="button" variant="body2" onClick={downloadQR} sx={{ display: 'block', mt: 1 }}>Tải QR Code</Link>
                        <TextField fullWidth size="small" label="Link" value={'https://youteach.ai/pages/bot?id=' + String(vector) + '&project=' + (getDocFullName(doc) || '').replaceAll('/', '')} sx={{ mt: 2 }} InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={handleCopyLink}><ContentCopyTwoToneIcon /></IconButton><IconButton size="small" href={'https://youteach.ai/pages/bot?id=' + String(vector) + '&project=' + (getDocFullName(doc) || '').replaceAll('/', '')} target="_blank" rel="noopener"><OpenInNewIcon /></IconButton></InputAdornment> }} />
                    </Box>
                </Popover>
            </Box>

            <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {/* Sidebar lịch sử: có thể thu gọn khi rỗng */}
                <Box
                    sx={{
                        width: historySidebarCollapsed ? 52 : 280,
                        minWidth: historySidebarCollapsed ? 52 : 280,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        transition: 'width 0.2s ease, min-width 0.2s ease'
                    }}
                >
                    {historySidebarCollapsed ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.5 }}>
                            <Tooltip title="Mở panel lịch sử" placement="right">
                                <IconButton size="small" onClick={() => setHistorySidebarCollapsed(false)} aria-label="Mở lịch sử">
                                    <ChevronRightIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Lịch sử" placement="right">
                                <HistoryIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 1 }} />
                            </Tooltip>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                    Lịch sử
                                </Typography>
                                <Tooltip title="Thu gọn panel" placement="left">
                                    <IconButton size="small" onClick={() => setHistorySidebarCollapsed(true)} aria-label="Thu gọn">
                                        <ChevronLeftIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <ScrollArea>
                                {selectedKnowledge
                                    ? (Array.isArray(researchConversations) && researchConversations.length > 0
                                        ? researchConversations.map((c) => (
                                            <ConversationTile
                                                key={c.id}
                                                conversation={{ id: c.id, name: c.title || 'Cuộc trò chuyện' }}
                                                selectConversation={() => handleResearchConversationClick(c.id)}
                                                onDeleteConversation={async (id) => {
                                                    try {
                                                        await deleteResearchConversation(id).unwrap();
                                                        refetchResearchConversations();
                                                        if (conversationId === id) {
                                                            dispatch(setConversation([]));
                                                            dispatch(updateConversationId({ query: { conversationId: null } }));
                                                        }
                                                    } catch (e) {
                                                        console.error(e);
                                                    }
                                                }}
                                                onSave={() => {}}
                                            />
                                        ))
                                        : (
                                            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Chưa có cuộc hội thoại. Hỏi câu đầu tiên để lưu lịch sử.
                                                </Typography>
                                                <Typography variant="caption" color="text.disabled" display="block">
                                                    Lịch sử được lưu trên máy chủ (Anthropic tính phí nên lưu lại để tái sử dụng).
                                                </Typography>
                                            </Box>
                                        )
                                    )
                                    : (conversations?.length > 0
                                        ? conversations.map((conversation) => (
                                            <ConversationTile
                                                key={conversation.id}
                                                conversation={conversation}
                                                selectConversation={(id) => handleConversationClick(id)}
                                                onDeleteConversation={(id) => handleDeleteConversation(id)}
                                                onSave={(c) => updateConversationName(c)}
                                            />
                                        ))
                                        : (
                                            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Chưa có cuộc hội thoại được lưu.
                                                </Typography>
                                                <Typography variant="caption" color="text.disabled" display="block">
                                                    Lịch sử chat hiện chưa lưu trên máy chủ. Tin nhắn trong phiên vẫn hiển thị bên cạnh.
                                                </Typography>
                                            </Box>
                                        )
                                    )
                                }
                            </ScrollArea>
                        </>
                    )}
                </Box>

                {/* Vùng chat: flex + minHeight 0 để scroll trong vùng cố định, tránh lag khi response dài */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', bgcolor: 'grey.50', position: 'relative' }}>
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                        <ScrollArea containerRef={(ref) => { scrollContainerRef.current = ref; setScrollEl(ref); }}>
                            <Box sx={{ p: 2, pb: 4, maxWidth: 1120, width: '100%', mx: 'auto', px: { xs: 1, sm: 2, md: 3 } }}>
                            {queries.length > 0 && queries.map((query, index) => (
                                <Box key={index} sx={{ mb: 2 }}>
                                    <ConversationBubble className="mb-2" key={`${index}QUESTION`} message={query.prompt ?? ''} type="QUESTION" sources={query.sources || []} />
                                    {prepResponseView(query, index)}
                                </Box>
                            ))}
                            {queries.length === 0 && (
                                <Hero
                                    onSuggestionClick={(text) => {
                                        if (!canSend && !canSendKnowledge) {
                                            showSnackbar({
                                                open: true,
                                                anchorOrigin: { vertical: 'top', horizontal: 'center' },
                                                message: 'Vui lòng chọn Knowledge hoặc trợ lý / dự án trước khi hỏi.',
                                                variant: 'alert',
                                                alert: { color: 'error' }
                                            });
                                            return;
                                        }
                                        handleQuestion(text, model);
                                    }}
                                />
                            )}
                            {status === 'loading' && queries.length > 0 && !queries[queries.length - 1].response && !queries[queries.length - 1].error && (
                                <Box sx={{ mt: 1 }}>
                                    <ConversationBubble
                                        className="mb-2"
                                        key="loading-answer"
                                        message="Đang phân tích tài liệu và tạo câu trả lời"
                                        type="ANSWER"
                                        sources={[]}
                                        isLoading
                                    />
                                </Box>
                            )}
                            {previewImageUrl && (
                                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Minh họa UX/UI (PNG)</Typography>
                                    <img
                                        src={previewImageUrl}
                                        alt="UX/UI Preview"
                                        style={{ maxWidth: '100%', height: 'auto', borderRadius: 8, display: 'block' }}
                                    />
                                </Box>
                            )}
                            <span ref={scrollRefChat} />
                            </Box>
                        </ScrollArea>
                        {/* Nút lên đầu / xuống cuối trang */}
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 16,
                                right: 16,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                                zIndex: 10
                            }}
                        >
                            <Tooltip title="Lên đầu trang" placement="left">
                                <IconButton
                                    size="small"
                                    onClick={() => scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
                                    sx={{ bgcolor: 'background.paper', boxShadow: 2, '&:hover': { bgcolor: 'action.hover' } }}
                                    aria-label="Lên đầu trang"
                                >
                                    <KeyboardArrowUpIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Xuống cuối trang" placement="left">
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        const el = scrollContainerRef.current;
                                        if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
                                    }}
                                    sx={{ bgcolor: 'background.paper', boxShadow: 2, '&:hover': { bgcolor: 'action.hover' } }}
                                    aria-label="Xuống cuối trang"
                                >
                                    <KeyboardArrowDownIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', p: 2 }}>
                        {attachmentFiles.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                {attachmentFiles.map((a) => (
                                    <Chip
                                        key={a.id}
                                        size="small"
                                        label={a.name}
                                        onDelete={() => setAttachmentFiles((prev) => prev.filter((x) => x.id !== a.id))}
                                        sx={{ maxWidth: 180 }}
                                    />
                                ))}
                            </Box>
                        )}
                        <input
                            type="file"
                            ref={attachmentFileInputRef}
                            multiple
                            accept="image/*,.pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.json"
                            style={{ display: 'none' }}
                            onChange={async (e) => {
                                const files = e.target.files ? Array.from(e.target.files) : [];
                                e.target.value = '';
                                if (!files.length) return;
                                for (const file of files.slice(0, 10)) {
                                    try {
                                        const res = await uploadStorageFile({ file }).unwrap();
                                        if (res?.id) setAttachmentFiles((prev) => [...prev, { id: res.id, name: file.name || 'file' }]);
                                    } catch (err) {
                                        showSnackbar({ open: true, message: `Không tải được ${file.name}`, variant: 'alert', alert: { color: 'error' } });
                                    }
                                }
                            }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, flexWrap: 'wrap' }}>
                            {selectedKnowledge && knowledgeDocuments?.length > 0 && (
                                <Tooltip title="Chọn file trong Knowledge để chỉnh sửa theo prompt (câu hỏi của bạn)" placement="top">
                                    <Box sx={{ minWidth: 140 }}>
                                        <select
                                            value={editFileId || ''}
                                            onChange={(e) => setEditFileId(e.target.value || null)}
                                            style={{
                                                fontSize: 12,
                                                padding: '6px 8px',
                                                borderRadius: 8,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                background: 'background.paper',
                                                color: 'text.primary',
                                                maxWidth: '100%'
                                            }}
                                        >
                                            <option value="">Không sửa file</option>
                                            {knowledgeDocuments.map((doc) => (
                                                <option key={doc.id} value={doc.storage_file_id || doc.id}>
                                                    Sửa: {doc.file_name || doc.id}
                                                </option>
                                            ))}
                                        </select>
                                    </Box>
                                </Tooltip>
                            )}
                            <Tooltip title={selectedKnowledge ? 'Đính kèm file hoặc ảnh để hỏi (giống Claude)' : 'Chọn Knowledge để dùng đính kèm'} placement="top">
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={!selectedKnowledge}
                                        onClick={() => attachmentFileInputRef.current?.click()}
                                        aria-label="đính kèm file"
                                    >
                                        <AttachFileIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <ChatInput
                                ref={chatInputRef}
                                onSubmit={(text) => handleQuestion(text, model)}
                                disabled={!canSend && !canSendKnowledge}
                                placeholder=""
                                status={status}
                                isRecording={isRecording}
                                isMicro={isMicro}
                                onStartRecord={start}
                                onStopRecord={stop}
                                showSnackbar={showSnackbar}
                                inputRef={inputRef}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
            {selectedKnowledge ? (
                <KnowledgeDocsManager open={manageDocsOpen} onClose={() => { setManageDocsOpen(false); setManageDocsKnowledge(null); }} knowledge={manageDocsKnowledge || selectedKnowledge} projectId={project} />
            ) : (
                <EmbeddingDocsManager open={manageDocsOpen} onClose={() => setManageDocsOpen(false)} doc={doc} userToken={localStorage.getItem('user_token')} />
            )}
        </MainCard>
    );
}
